import { useState } from 'react';
import { getDefaultClassNames, DayPicker as ReactDayPicker } from 'react-day-picker';
import { ko } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { Icon } from '../icon/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib/utils';
import './styles.css';

interface DayPickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  hrizontalOptions?: string;
  placeholder?: string;
}

export function DayPicker({ value, onChange, hrizontalOptions, placeholder }: DayPickerProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Date | undefined>(value);
  const defaultClassNames = getDefaultClassNames();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className={`flex ${hrizontalOptions === 'between' ? 'justify-between' : 'justify-center'} items-center gap-2 w-fit h-10 px-4 py-2.5 bg-[#F5F5F5] rounded-lg text-sm tracking-[-0.1px]`}
          style={{ color: selected ? '#101010' : '#BFBFBF' }}
        >
          {selected
            ? `${selected?.getFullYear().toString()}.${((selected?.getMonth() || 0) + 1).toString().padStart(2, '0')}.${selected?.getDate().toString().padStart(2, '0')}`
            : placeholder || `2025.01.01`}
          <Icon name='CalendarFilled' size={24} color='#101010' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-fit border-none' align='start'>
        <div className='custom-day-picker'>
          <ReactDayPicker
            mode='single'
            selected={selected}
            onSelect={(date) => {
              setSelected(date);
              onChange(date);
              setOpen(false);
            }}
            timeZone='Asia/Seoul'
            locale={ko}
            classNames={{
              ...defaultClassNames,
              month_caption: cn('text-size-3 font-bold leading-font-height-3 text-black mb-4'),
              weekdays: cn('my-2'),
              weekday: cn('px-2 py-0.5 text-size-5 font-bold leading-font-height-5 text-black'),
              day: cn(
                'text-size-5 font-regular leading-font-height-5 tracking-[-0.1px]',
                'has-[.saturday]:text-light-blue-300!',
                'data-[selected=true]:text-text-on-color!',
              ),
              day_button: cn(
                `${defaultClassNames.day_button} border-0! font-regular leading-font-height-5 tracking-[-0.1px]`,
              ),
              today: cn('text-size-5! font-bold'),
              selected: cn('bg-surface-black rounded-full'),
            }}
            modifiers={{
              saturday: (day) => day.getDay() === 6,
              sunday: (day) => day.getDay() === 0,
            }}
            modifiersClassNames={{
              saturday: 'saturday',
              sunday: 'sunday',
            }}
            components={{
              Nav: ({ onNextClick, onPreviousClick }) => (
                <div className='flex items-center gap-2 absolute h-6 top-0.5 right-0'>
                  <button type='button' onClick={onPreviousClick}>
                    <Icon name='ChevronLeft' size={24} color='#1a1a1a' />
                  </button>
                  <button type='button' onClick={onNextClick}>
                    <Icon name='ChevronRight' size={24} color='#1a1a1a' />
                  </button>
                </div>
              ),
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
